import moment from "moment";
import { FC } from "react";
import { Badge, Button, Modal, Text, Title } from "rizzui";
import { Pickup } from "../../../type/entities/pickup-type";
import { pickupStatuses } from "../../../utils/SelectorMapping";

interface AssignPickupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    pickup: Pickup | null;
    isLoading?: boolean;
}

const AssignPickupModal: FC<AssignPickupModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    pickup,
    isLoading = false
}) => {
    if (!pickup) return null;

    const getStatusColor = (status: string) => {
        const statusObj = pickupStatuses.find(s => s.value === status);
        return statusObj?.color || '#6B7280';
    };

    const getStatusLabel = (status: string) => {
        const statusObj = pickupStatuses.find(s => s.value === status);
        return statusObj?.label || status;
    };

    // Datos actuales (recogida fallida)
    const currentData = {
        status: pickup.pickupStatus,
        date: pickup.pickupDate?.date,
        assignedTo: pickup.user?.name || 'Sin asignar',
        location: `${pickup.headquarter?.name || ''} - ${pickup.headquarter?.city || ''}`,
        route: pickup.route?.name || 'Sin ruta'
    };

    // Nuevos datos (con fecha actual pero hora original)
    const newData = {
        status: 'started_by_driver',
        date: moment().format('YYYY-MM-DD') + ' ' + moment(pickup.pickupDate?.date).format('HH:mm:ss'),
        assignedTo: 'Tú',
        location: currentData.location,
        route: currentData.route
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="p-6">
                <div className="mb-6">
                    <Title as="h3" className="text-lg font-semibold text-gray-900 mb-2">
                        Confirmar Asignación de Recogida
                    </Title>
                    <Text className="text-sm text-gray-600">
                        Revisa los cambios que se aplicarán al asignar esta recogida
                    </Text>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Datos Actuales */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <Title as="h4" className="text-sm font-medium text-red-800">
                                Estado Actual
                            </Title>
                        </div>
                        
                        <div className="space-y-3">
                            <div>
                                <Text className="text-xs text-red-600 font-medium mb-1">Estado</Text>
                                <Badge
                                    variant="flat"
                                    style={{ 
                                        backgroundColor: `${getStatusColor(currentData.status)}20`, 
                                        color: getStatusColor(currentData.status) 
                                    }}
                                    className="text-xs"
                                >
                                    {getStatusLabel(currentData.status)}
                                </Badge>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-red-600 font-medium mb-1">Fecha y Hora</Text>
                                <Text className="text-sm text-red-800">
                                    {moment(currentData.date).format('DD/MM/YYYY HH:mm')}
                                </Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-red-600 font-medium mb-1">Asignado a</Text>
                                <Text className="text-sm text-red-800">{currentData.assignedTo}</Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-red-600 font-medium mb-1">Ubicación</Text>
                                <Text className="text-sm text-red-800">{currentData.location}</Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-red-600 font-medium mb-1">Ruta</Text>
                                <Text className="text-sm text-red-800">{currentData.route}</Text>
                            </div>
                        </div>
                    </div>

                    {/* Nuevos Datos */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <Title as="h4" className="text-sm font-medium text-green-800">
                                Nuevo Estado
                            </Title>
                        </div>
                        
                        <div className="space-y-3">
                            <div>
                                <Text className="text-xs text-green-600 font-medium mb-1">Estado</Text>
                                <Badge
                                    variant="flat"
                                    style={{ 
                                        backgroundColor: `${getStatusColor(newData.status)}20`, 
                                        color: getStatusColor(newData.status) 
                                    }}
                                    className="text-xs"
                                >
                                    {getStatusLabel(newData.status)}
                                </Badge>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-green-600 font-medium mb-1">Fecha y Hora</Text>
                                <Text className="text-sm text-green-800">
                                    {moment(newData.date).format('DD/MM/YYYY HH:mm')}
                                </Text>
                                <Text className="text-xs text-green-600 mt-1">
                                    * Fecha actual, hora original mantenida
                                </Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-green-600 font-medium mb-1">Asignado a</Text>
                                <Text className="text-sm text-green-800">{newData.assignedTo}</Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-green-600 font-medium mb-1">Ubicación</Text>
                                <Text className="text-sm text-green-800">{newData.location}</Text>
                                <Text className="text-xs text-green-600 mt-1">* Sin cambios</Text>
                            </div>
                            
                            <div>
                                <Text className="text-xs text-green-600 font-medium mb-1">Ruta</Text>
                                <Text className="text-sm text-green-800">{newData.route}</Text>
                                <Text className="text-xs text-green-600 mt-1">* Sin cambios</Text>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Información adicional */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs font-bold">i</span>
                        </div>
                        <div>
                            <Text className="text-sm text-blue-800 font-medium mb-1">
                                Información importante
                            </Text>
                            <Text className="text-xs text-blue-700">
                                • La recogida se asignará a tu lista de "Mis recogidas"<br/>
                                • Se mantendrá la hora original de la recogida<br/>
                                • Se actualizará la fecha de asignación al momento actual<br/>
                                • Podrás ejecutar la recogida inmediatamente
                            </Text>
                        </div>
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                        {isLoading ? 'Asignando...' : 'Confirmar Asignación'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AssignPickupModal;